import { useEffect, useState } from 'react';
import { Check, Clock, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { BillingPageLayout } from './components/BillingPageLayout';
import { useEntity } from '@/hooks/useEntity';
import { clearPurchaseIntent, peekJourneyRoute, peekPurchaseIntent } from '@/lib/auth/intent-storage';
import { ROUTES } from '@/lib/constants/routes';
import { formatDate } from '@/lib/utils/date.utils';
import { formatCents } from '@/lib/utils/format.utils';
import { getCheckoutReturn } from './api/billing.api';
import { BILLING_STRINGS } from './constants/billing-strings';
import { billingKeys } from './hooks/billing.keys';

const COPY = BILLING_STRINGS.RETURN;

/**
 * The two markers the Backend puts on the Stripe session's return URLs
 * (`checkout.service.ts`). `session` means the member came back from a
 * COMPLETED checkout; `cancelled` means they backed out. Neither is sent to
 * the API - the backend resolves state from the startup's own subscription -
 * they only decide which of the three return states this screen renders.
 */
const CANCELLED_PARAM = 'cancelled';
const SESSION_PARAM = 'session';
/** The billing basis whose date marks the end of access rather than a renewal. */
const ONE_TIME = 'one_time';

/** How often to re-ask while the confirming webhook is still in flight. */
const PENDING_POLL_MS = 2000;
/**
 * When to stop asking: a minute. The webhook is normally seconds behind; past
 * a minute something is stuck (a queue down, a handler failing), and polling
 * forever would leave "finalising" on screen indefinitely.
 */
export const PENDING_TIMEOUT_MS = 60_000;

/**
 * Where Stripe sends the browser back (FR-39, A-73).
 *
 * ## Pending is a state, not a spinner
 *
 * The webhook that confirms the purchase is processed on a queue, so a member
 * who returns promptly arrives BEFORE the state that grants entitlement exists.
 * The old design asserted "Your DAXE Data Room is ready and your 2 seats are
 * activated" unconditionally; doing that here would tell a member their data
 * room is ready seconds before it refuses them.
 *
 * So this page renders what the backend can actually see, and polls while the
 * answer is `pending`. Polling rather than waiting once: the queue is normally
 * a second or two behind, and a member watching a permanent "finalising" panel
 * with no resolution would reasonably conclude their money went nowhere.
 *
 * ## The failure copy is deliberately generic
 *
 * A-75. With hosted Checkout, Stripe handles declines inside its own page and
 * does not redirect back with a decline code, so the card-level reasons the
 * design enumerated (insufficient funds, expired card) cannot be supplied.
 * Promising that specificity would be a lie the integration cannot keep.
 */
export function CheckoutReturnPage() {
  return (
    <BillingPageLayout>
      <CheckoutReturnContent />
    </BillingPageLayout>
  );
}

/** Which of the return states to render; the layout above is shared by all of them. */
function CheckoutReturnContent() {
  const entity = useEntity();
  const entityId = entity?.entityId;
  const [params] = useSearchParams();
  const hasSession = params.has(SESSION_PARAM);
  const wasCancelled = params.has(CANCELLED_PARAM);
  const [timedOut, setTimedOut] = useState(false);

  const result = useQuery({
    queryKey: billingKeys.checkoutReturn(entityId ?? 'none'),
    queryFn: () => getCheckoutReturn(entityId as string),
    enabled: Boolean(entityId && hasSession && !wasCancelled),
    // Stop polling the moment the backend has an answer, or once it has
    // stayed pending past the limit.
    refetchInterval: (query) =>
      query.state.data?.state === 'pending' && !timedOut ? PENDING_POLL_MS : false,
  });

  // Read once: the confirmed purchase ends the journey and forgets the intent
  // (FR-206), but this screen still shows the completed progress bar (FR-224).
  const [inJourney] = useState(() => peekPurchaseIntent() !== null);
  const [route] = useState(peekJourneyRoute);
  const isConfirmed = result.data?.state === 'confirmed';
  // A cancelled checkout ends the journey too, so the intent does not linger.
  useEffect(() => {
    if (isConfirmed || wasCancelled) clearPurchaseIntent();
  }, [isConfirmed, wasCancelled]);

  const isPending = result.data?.state === 'pending';
  useEffect(() => {
    if (!isPending) return undefined;
    const timer = setTimeout(() => setTimedOut(true), PENDING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPending]);

  if (wasCancelled) {
    return (
      <Panel tone="stopped" title={COPY.CANCELLED_TITLE} body={COPY.CANCELLED_BODY}>
        <Button asChild className="inline-flex w-full justify-center">
          <Link to={ROUTES.BILLING_PURCHASE}>{COPY.TRY_AGAIN}</Link>
        </Button>
      </Panel>
    );
  }

  // Arrived with neither marker: a bookmark, or a redirect that lost its query.
  // Nothing was purchased in THIS visit, and inventing an outcome - in either
  // direction - would be worse than saying so.
  if (!hasSession) return <EmptyState title={COPY.MISSING_SESSION} />;

  if (result.isLoading) return <LoadingSpinner label={COPY.PENDING_TITLE} />;

  if (result.isError) {
    return (
      <Panel tone="stopped" title={COPY.FAILED_TITLE} body={COPY.FAILED_BODY}>
        <Button asChild className="inline-flex w-full justify-center">
          <Link to={ROUTES.BILLING_PURCHASE}>{COPY.TRY_AGAIN}</Link>
        </Button>
      </Panel>
    );
  }

  if (result.data?.state === 'pending') {
    if (timedOut) {
      return (
        <Panel tone="waiting" title={COPY.PENDING_TIMEOUT_TITLE} body={COPY.PENDING_TIMEOUT_BODY}>
          <Button asChild className="inline-flex w-full justify-center">
            <Link to={ROUTES.BILLING}>{COPY.GO_TO_BILLING}</Link>
          </Button>
        </Panel>
      );
    }
    return <Panel tone="waiting" title={COPY.PENDING_TITLE} body={COPY.PENDING_BODY} />;
  }

  const confirmed = result.data;
  if (!confirmed || confirmed.state !== 'confirmed') return null;

  const rows = [
    { label: COPY.SUMMARY_PLAN, value: confirmed.planName },
    ...(confirmed.amountPaidCents !== null
      ? [{ label: COPY.SUMMARY_AMOUNT, value: formatCents(confirmed.amountPaidCents) ?? '' }]
      : []),
    ...(confirmed.currentPeriodEnd
      ? [
          {
            label: confirmed.billingInterval === ONE_TIME ? COPY.ACCESS_ENDS : COPY.NEXT_BILLING,
            value: formatDate(confirmed.currentPeriodEnd),
          },
        ]
      : []),
    { label: COPY.SUMMARY_SEATS, value: String(confirmed.includedSeats) },
  ];

  return (
    <>
      {inJourney && <IssueReportJourneySteps stage="done" route={route} />}
      <Panel tone="done" title={COPY.CONFIRMED_TITLE} body={COPY.CONFIRMED_BODY}>
        <dl className="bg-surface-raised rounded-card flex w-full flex-col gap-3 p-5 text-sm">
          {rows.map((row) => (
            <div key={row.label} className="flex justify-between gap-4">
              <dt className="text-text-muted">{row.label}</dt>
              <dd className="text-text-primary text-right font-semibold">{row.value}</dd>
            </div>
          ))}
        </dl>
        {/* Straight to the Data Room, not back to the dashboard: the buyer has
          just paid for it (UX round 3, StartupAdminPatty Y12). */}
        <div className="flex w-full flex-col gap-3">
          <Button asChild className="inline-flex w-full justify-center">
            <Link to={ROUTES.DATA_ROOM}>{COPY.OPEN_DATA_ROOM}</Link>
          </Button>
          <Button asChild variant="link" className="inline-flex justify-center">
            <Link to={ROUTES.BILLING}>{COPY.GO_TO_BILLING}</Link>
          </Button>
        </div>
      </Panel>
    </>
  );
}

/** Each outcome's icon and colour, resolved once per tone. */
type PanelTone = 'done' | 'stopped' | 'waiting';
const TONE: Record<PanelTone, { icon: typeof Check; circle: string }> = {
  done: { icon: Check, circle: 'bg-green text-white' },
  stopped: { icon: X, circle: 'bg-red text-white' },
  waiting: { icon: Clock, circle: 'bg-yellow/20 text-navy' },
};

/**
 * The one card all the return states share: an icon that says the outcome at
 * a glance, a title, what it means, then what to do (2026-09-22 design).
 */
function Panel({
  tone,
  title,
  body,
  children,
}: {
  tone: PanelTone;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  const { icon: Icon, circle } = TONE[tone];
  return (
    <div className="rounded-card mx-auto flex w-full max-w-lg flex-col items-center gap-5 bg-white p-10 text-center shadow-card">
      <span className={`flex h-16 w-16 items-center justify-center rounded-full ${circle}`}>
        <Icon aria-hidden="true" className="h-8 w-8" />
      </span>
      <div>
        <h2 className="text-text-primary text-2xl font-semibold">{title}</h2>
        <p className="text-text-secondary mt-2 text-sm">{body}</p>
      </div>
      {children}
    </div>
  );
}
